import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  AcademicCapIcon,
  UsersIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  UserIcon,
  AtSymbolIcon,
  PhoneIcon,
  IdentificationIcon,
  BuildingLibraryIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  DevicePhoneMobileIcon,
} from "@heroicons/react/24/outline";
import logo from "../../assets/logo.svg";
import bannerSchool from "../../assets/banner-school.png";

/* ── Field helper ── */
function Field({
  Icon, label, type = "text", placeholder, hint,
}: {
  Icon: React.ElementType; label: string;
  type?: string; placeholder: string; hint?: string;
}) {
  return (
    <div>
      <label className="block text-dark/70 text-xs font-semibold mb-1.5">{label}</label>
      <div className="flex items-center gap-3 border-2 border-border rounded-xl px-4 py-3 bg-white focus-within:border-blue transition-colors">
        <Icon className="w-4 h-4 text-muted shrink-0" />
        <input
          type={type}
          placeholder={placeholder}
          className="flex-1 text-sm text-dark placeholder:text-muted/60 outline-none bg-transparent"
        />
      </div>
      {hint && <p className="text-muted text-[11px] mt-1 ml-1">{hint}</p>}
    </div>
  );
}

/* ── Left panel (fixed across all steps) ── */
function LeftPanel() {
  return (
    <div
      className="relative flex-1 flex flex-col justify-between px-14 py-12 overflow-hidden max-md:hidden"
      style={{
        backgroundImage: `url(${bannerSchool})`,
        backgroundSize: "cover",
        backgroundPosition: "center top",
      }}
    >
      <div className="absolute inset-0" style={{ background: "linear-gradient(160deg, rgba(0,22,39,0.93) 0%, rgba(0,40,73,0.82) 50%, rgba(5,105,163,0.68) 100%)" }} />
      <div className="absolute bottom-0 left-0 w-full h-48 pointer-events-none" style={{ background: "linear-gradient(to top, rgba(0,22,39,0.88), transparent)" }} />

      <div className="relative">
        <Link to="/"><img src={logo} alt="PIA" className="h-9" /></Link>
      </div>

      <div className="relative flex-1 flex flex-col justify-center py-12 max-w-sm">
        <span className="text-blue-light text-xs font-semibold uppercase tracking-widest mb-4">
          Criar conta de utilizador
        </span>
        <h1 className="text-4xl font-extrabold text-white leading-[1.15] mb-5">
          Pague propinas{" "}
          <span className="text-transparent bg-clip-text" style={{ backgroundImage: "linear-gradient(90deg, #2babe3, #7dd3fc)" }}>
            onde estiver
          </span>
        </h1>
        <p className="text-white/50 text-sm leading-relaxed mb-10">
          Registe-se como estudante ou encarregado para gerir e efectuar pagamentos de forma simples e segura.
        </p>

        <div className="flex flex-col gap-3">
          {[
            { Icon: ShieldCheckIcon,       t: "Pagamentos 100% seguros" },
            { Icon: CurrencyDollarIcon,    t: "Sem taxas ocultas" },
            { Icon: DevicePhoneMobileIcon, t: "Acesso pelo telemóvel ou PC" },
          ].map(({ Icon, t }) => (
            <div key={t} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-white/8 flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4 text-blue-light" />
              </div>
              <span className="text-white/60 text-sm">{t}</span>
            </div>
          ))}
        </div>

        <div className="mt-10 inline-flex items-center gap-3 bg-white/8 backdrop-blur-sm border border-white/12 rounded-2xl px-5 py-4 w-fit">
          <CheckCircleIcon className="w-5 h-5 text-emerald-400 shrink-0" />
          <span className="text-white/70 text-sm">Mais de 50.000 utilizadores registados</span>
        </div>
      </div>

      <p className="relative text-white/25 text-xs">© 2024 PIA · Todos os direitos reservados</p>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   MAIN
══════════════════════════════════════════════════ */
export default function UserRegister() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [typeUser, setTypeUser] = useState(
    () => localStorage.getItem("usertype") || "encarregado"
  );

  const selectType = (val: string) => {
    setTypeUser(val);
    localStorage.setItem("usertype", val);
  };

  /* Step indicator */
  const totalSteps = 2;
  const currentStep = id === "1" ? 1 : 2;

  return (
    <div className="min-h-screen flex max-md:flex-col">
      <LeftPanel />

      <div className="w-px bg-border max-md:hidden" />

      {/* ── Right panel ── */}
      <div className="flex-1 bg-off-white flex flex-col justify-center px-14 py-16 max-md:px-8 max-md:py-20">

        {/* Mobile logo */}
        <div className="hidden max-md:block mb-10">
          <Link to="/"><img src={logo} alt="PIA" className="h-8" /></Link>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-10">
          {Array.from({ length: totalSteps }).map((_, i) => {
            const n = i + 1;
            const done = n < currentStep;
            const cur  = n === currentStep;
            return (
              <div key={n} className="flex items-center gap-2">
                <div className="flex flex-col items-center gap-1">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all"
                    style={
                      done ? { background: "linear-gradient(135deg,#10b981,#059669)", color: "#fff" }
                      : cur ? { background: "linear-gradient(135deg,#2babe3,#016fb9)", color: "#fff" }
                      : { background: "#e2eaf2", color: "#9fb8cc" }
                    }
                  >
                    {done ? <CheckCircleIcon className="w-4 h-4" /> : n}
                  </div>
                  <span className={`text-[10px] font-semibold ${cur ? "text-blue" : done ? "text-emerald-500" : "text-muted/50"}`}>
                    {n === 1 ? "Tipo" : typeUser === "encarregado" ? "Encarregado" : "Estudante"}
                  </span>
                </div>
                {i < totalSteps - 1 && (
                  <div className="h-0.5 w-10 rounded-full mb-4 transition-all" style={{ background: done ? "#10b981" : "#e2eaf2" }} />
                )}
              </div>
            );
          })}
          <span className="ml-2 text-muted text-xs">Passo {currentStep} de {totalSteps}</span>
        </div>

        {/* ─── STEP 1: escolha de tipo ─── */}
        {id === "1" && (
          <>
            <p className="text-muted text-xs font-semibold uppercase tracking-widest mb-2">Registo de utilizador</p>
            <h2 className="text-2xl font-extrabold text-dark mb-1">Quem é você?</h2>
            <p className="text-muted text-sm mb-8">Seleccione o tipo de conta que deseja criar</p>

            <div className="flex flex-col gap-4 mb-10">
              {[
                {
                  val: "estudante",
                  Icon: AcademicCapIcon,
                  title: "Estudante",
                  subtitle: "Universitário · Ensino Médio · Primário",
                  desc: "Efectue o pagamento das suas propinas directamente da sua conta, a qualquer hora.",
                },
                {
                  val: "encarregado",
                  Icon: UsersIcon,
                  title: "Encarregado de Educação",
                  subtitle: "Pai · Mãe · Tutor",
                  desc: "Pague as propinas dos seus educandos e receba notificações em tempo real.",
                },
              ].map(({ val, Icon, title, subtitle, desc }) => {
                const sel = typeUser === val;
                return (
                  <label
                    key={val}
                    onClick={() => selectType(val)}
                    className={`flex items-start gap-4 p-5 rounded-2xl border-2 cursor-pointer transition-all duration-200 ${
                      sel
                        ? "border-blue bg-blue/5 shadow-md shadow-blue/10"
                        : "border-border bg-white hover:border-blue/40 hover:shadow-sm"
                    }`}
                  >
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                      style={sel ? { background: "linear-gradient(135deg,#2babe3,#016fb9)" } : { background: "#ebf5fb" }}
                    >
                      <Icon className={`w-5 h-5 ${sel ? "text-white" : "text-blue"}`} />
                    </div>
                    <div className="flex-1 min-w-0 pt-0.5">
                      <p className={`font-bold text-sm mb-0.5 ${sel ? "text-blue" : "text-dark"}`}>{title}</p>
                      <p className={`text-[11px] font-semibold mb-1.5 ${sel ? "text-blue/60" : "text-muted"}`}>{subtitle}</p>
                      <p className={`text-xs leading-relaxed ${sel ? "text-dark/65" : "text-muted"}`}>{desc}</p>
                    </div>
                    <div
                      className="w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5"
                      style={sel ? { background: "linear-gradient(135deg,#2babe3,#016fb9)", borderColor: "transparent" } : { borderColor: "#cbe4f5" }}
                    >
                      {sel && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                  </label>
                );
              })}
            </div>

            <Link
              to={`/register-user/${typeUser === "encarregado" ? "2" : "3"}`}
              className="flex items-center justify-center gap-2 w-full py-4 rounded-2xl text-white font-bold text-sm transition-all hover:opacity-90 hover:scale-[1.01]"
              style={{ background: "linear-gradient(90deg,#2babe3,#016fb9)" }}
            >
              Avançar
              <ArrowRightIcon className="w-4 h-4" />
            </Link>

            <p className="text-center text-muted text-xs mt-6">
              Já tem conta?{" "}
              <Link to="/access-user" className="text-blue font-semibold hover:text-blue-light transition-colors">
                Entrar aqui
              </Link>
            </p>
          </>
        )}

        {/* ─── STEP 2: Encarregado ─── */}
        {id === "2" && (
          <>
            <p className="text-muted text-xs font-semibold uppercase tracking-widest mb-2">Encarregado de educação</p>
            <h2 className="text-2xl font-extrabold text-dark mb-1">Criar a sua conta</h2>
            <p className="text-muted text-sm mb-8">Preencha os seus dados pessoais para solicitar acesso</p>

            <form className="flex flex-col gap-4" onSubmit={(e) => e.preventDefault()}>
              <Field Icon={UserIcon}    label="Primeiro e último nome" placeholder="Ex: Maria Santos" />
              <Field Icon={AtSymbolIcon} label="E-mail"               placeholder="maria@exemplo.ao" type="email" />
              <Field Icon={PhoneIcon}   label="Número de telefone"    placeholder="+244 9XX XXX XXX"
                hint="Será usado para notificações de pagamento" />

              <div className="flex gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => navigate("/register-user/1")}
                  className="flex items-center gap-2 px-6 py-4 rounded-2xl border-2 border-border text-dark font-semibold text-sm hover:border-blue hover:text-blue transition-all"
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                  Voltar
                </button>
                <button
                  type="submit"
                  className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl text-white font-bold text-sm transition-all hover:opacity-90"
                  style={{ background: "linear-gradient(90deg,#2babe3,#016fb9)" }}
                >
                  Solicitar conta
                  <ArrowRightIcon className="w-4 h-4" />
                </button>
              </div>

              <p className="text-center text-muted text-xs mt-2">
                Já tem acesso?{" "}
                <Link to="/access-user" className="text-blue font-semibold hover:text-blue-light transition-colors">
                  Entrar aqui
                </Link>
              </p>
            </form>
          </>
        )}

        {/* ─── STEP 3: Estudante ─── */}
        {id === "3" && (
          <>
            <p className="text-muted text-xs font-semibold uppercase tracking-widest mb-2">Estudante</p>
            <h2 className="text-2xl font-extrabold text-dark mb-1">Criar a sua conta</h2>
            <p className="text-muted text-sm mb-8">Introduza os seus dados escolares para solicitar acesso</p>

            <form className="flex flex-col gap-4" onSubmit={(e) => e.preventDefault()}>
              <Field Icon={BuildingLibraryIcon} label="Instituição escolar"            placeholder="Nome da sua escola ou universidade" />
              <Field Icon={AcademicCapIcon}     label="Número de estudante"            placeholder="Ex: 2024-EST-0001"
                hint="Encontre-o no seu cartão de estudante" />
              <Field Icon={IdentificationIcon}  label="Número do bilhete de identidade" placeholder="Nº do seu BI" />
              <Field Icon={PhoneIcon}           label="Número de telefone"             placeholder="+244 9XX XXX XXX" />

              <div className="flex gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => navigate("/register-user/1")}
                  className="flex items-center gap-2 px-6 py-4 rounded-2xl border-2 border-border text-dark font-semibold text-sm hover:border-blue hover:text-blue transition-all"
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                  Voltar
                </button>
                <button
                  type="submit"
                  className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl text-white font-bold text-sm transition-all hover:opacity-90"
                  style={{ background: "linear-gradient(90deg,#2babe3,#016fb9)" }}
                >
                  Solicitar conta
                  <ArrowRightIcon className="w-4 h-4" />
                </button>
              </div>

              <p className="text-center text-muted text-xs mt-2">
                Já tem acesso?{" "}
                <Link to="/access-user" className="text-blue font-semibold hover:text-blue-light transition-colors">
                  Entrar aqui
                </Link>
              </p>
            </form>
          </>
        )}

      </div>
    </div>
  );
}
